import { useEffect, useRef, useState, type ImgHTMLAttributes, type ReactNode } from "react";
import { Skeleton } from "./Skeleton";

type CoverImageProps = Pick<
	ImgHTMLAttributes<HTMLImageElement>,
	"src" | "srcSet" | "sizes" | "alt" | "loading" | "fetchPriority"
> & {
	className?: string;
	fallback: ReactNode;
};

/** A new source gets its own decode state, including when a card is reused after an edit. */
export function CoverImage(props: CoverImageProps) {
	return <DecodedCover key={`${props.src}|${props.srcSet ?? ""}`} {...props} />;
}

function DecodedCover({ className = "", fallback, loading = "lazy", ...imageProps }: CoverImageProps) {
	const imageRef = useRef<HTMLImageElement>(null);
	const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading");

	async function reveal(image: HTMLImageElement) {
		try {
			await image.decode();
		} catch {
			// Some browsers reject decode for an otherwise usable, already loaded image.
		}
		if (imageRef.current === image && image.complete) {
			setStatus(image.naturalWidth > 0 ? "loaded" : "error");
		}
	}

	useEffect(() => {
		const image = imageRef.current;
		if (image?.complete) void reveal(image);
	}, []);

	return (
		<span className={`relative block overflow-hidden ${className}`} data-cover-state={status}>
			{status === "error" ? fallback : (
				<>
					{status === "loading" && <Skeleton className="absolute inset-0 size-full" />}
					<img
						{...imageProps}
						ref={imageRef}
						loading={loading}
						decoding="async"
						onLoad={(event) => void reveal(event.currentTarget)}
						onError={() => setStatus("error")}
						className={`size-full object-cover transition-opacity duration-200 motion-reduce:transition-none ${status === "loaded" ? "opacity-100" : "opacity-0"}`}
					/>
				</>
			)}
		</span>
	);
}
